import { api, post, protectedPatch, protectedPost } from "./restUtil";
import { saveFirebaseNotificationToken } from "./firebase/firebaseUtil";
import * as _ from "underscore";
import { initIntercom, shutDownIntercom } from "./intercom";
import { deleteRoles } from "./admin/roles";
import { fetchStudentProfile } from "./students";
import { fetchInstructorProfile } from "./instructors";
import { clearContentCredentials } from "./content";
import { saveUserContext, clearUserContext, logInfo } from "./logging";
import { getServerTimeOffset } from "./util";
import {
  handleServerTimeDifference,
  SERVER_TIME_DIFFERENCE,
} from "./adminAutoNotifications/helpers";
import Configuration from "./../config";
import getBrowserInfo from "./browserSupport";
import { trackSignIn, trackSignUp, trackUserId } from "./analytics";
import { MutableObservable } from "../utils/observable";
import { getUserProfile, logOut } from "services/users";
import { USER_STORAGE_KEY } from "utils/storage";
import { useNavigate } from "react-router";
import TalaeraRoutes from "components//routes/TalaeraRoutes";
import { useCallback } from "react";

const moment = require("moment-timezone");

export const BETA_ACCEPTED = "beta_accepted";
export const TEMP_NATIVE_LANG = "temp_native_lang";

const browser = getBrowserInfo();

export function signUp(data, onSuccess, onError) {
  return protectedPost(
    "/user/create",
    {
      email: data.email,
      passwd: data.password,
      first_name: data.firstName,
      last_name: data.lastName,
      city: data.place?.address?.city || data.city,
      country: data.place?.address?.country || data.country,
      timezone: data.place?.timezoneId || data.timezone,
      pricing_model_code: data.pricing_model_code,
      pricing_model_item_id: data.pricing_model_item_id,
      localization_lang: data.localization_lang || "en",
    },
    function (createResp) {
      trackSignUp(createResp.user.id);
      trackUserId(createResp.user.id);
      if (createResp.user.status === "active") {
        return signIn(
          data,
          function () {
            return protectedPatch(
              "/user/profile",
              {
                state: data.place.address.state,
                street1: data.place.address.street1,
                native_language: data.native_language ? data.native_language.code : null,
                title: data.title,
                company: data.company,
                company_website: data.company_website,
                phone_work: data.phone,
              },
              function () {
                populateUserSession(onSuccess, onError);
              },
              onError
            );
          },
          onError
        );
      } else {
        return onSuccess && onSuccess(createResp);
      }
    },
    function (errBody) {
      return onError(errBody);
    }
  );
}

export function resendVerifyMessage(args, onSuccess, OnError) {
  return protectedPost("/user/verify/resend", args, onSuccess, OnError, {
    includeStatusCode: true,
  });
}

export function signUpIndividual(data, onSuccess, onError) {
  return protectedPost(
    "/user/individual/create",
    {
      email: data.email,
      passwd: data.password,
      first_name: data.firstName,
      native_language: data.primaryLanguage?.code || "en_US",
      localization_lang: data.localization_lang || "en",
      country: data.countryCode?.code,
      is_pro: data.isPro || false,
      pricing_model_code: data.pricing_model_code,
    },
    function (createResp) {
      trackSignUp(createResp.user.id);
      if (createResp.user.status === "active") {
        populateUserSession(onSuccess, onError);
      } else {
        return onSuccess && onSuccess(createResp);
      }
    },
    function (errBody) {
      return onError(errBody);
    }
  );
}

function _signInFinish(resBody, onSuccess /*, onError*/) {
  trackSignIn(resBody.id);
  trackUserId(resBody.id);
  saveFirebaseNotificationToken(
    () => {
      return onSuccess && onSuccess(resBody);
    },
    (/*errors*/) => {
      // TODO it shouldn't be a problem to use an app without notifications, revisit
      // return onError && onError(resBody);
      return onSuccess && onSuccess(resBody);
    }
  );
}

export function signIn(data, onSuccess, onError) {
  return protectedPost(
    "/user/login",
    { email: data.email, passwd: data.password },
    resBody => {
      if (resBody.mfa) {
        // Multi-factor auth requires a next step, so here we don't initialize anything quite yet.
        return onSuccess && onSuccess(resBody);
      }
      return _signInFinish(resBody, onSuccess, onError);
    },
    onError,
    { includeStatusCode: true }
  );
}

export function signInSSO(data) {
  return new Promise((resolve, reject) => {
    protectedPost("/saml/login", { email: data.email }, resolve, reject);
  });
}

export function signInMfa(data, onSuccess, onError) {
  return protectedPost(
    "/user/login/mfa",
    { email: data.email, mfa_code: data.code, token: data.token },
    resBody => {
      // assert !resBody.mfa
      return _signInFinish(resBody, onSuccess, onError);
    },
    onError
  );
}

export function forgotPassword(args, onSuccess, onErrors) {
  return post("/user/passwd/uhm", args, onSuccess, onErrors);
}

export function verifyPassword(args, onSuccess, onErrors) {
  return post("/user/passwd/verify", args, onSuccess, onErrors);
}

export function verifyUser(args, additionalParams) {
  return api.post("/user/verify", args, additionalParams);
}

export function changePassword(args, onSuccess, onErrors) {
  return protectedPost("/user/passwd", args, onSuccess, onErrors);
}

export function isUserAuthenticated() {
  return getSessionUser() !== null;
}

export function populateUserSession(onSuccess, onError) {
  return getUserProfile()
    .then(resBody => {
      if (!resBody || !resBody.id || !resBody.profile || !resBody.roles) {
        return onError && onError("invalid response structure");
      }

      let user = {
        id: resBody.id,
        require_otp: resBody.require_otp,
        require_sms: resBody.require_sms,
        features: resBody.features,
        profile: {
          first_name: resBody.profile.first_name,
          last_name: resBody.profile.last_name,
          email: resBody.profile.email,
          title: resBody.profile.title,
          timezone: {
            id: resBody.profile.timezone.id,
            utc_offset: resBody.profile.timezone.utc_offset,
          },
          address: resBody.profile.address,
          native_language: resBody.profile.native_language,
          bio: resBody.profile.bio,
          liked_on_facebook: resBody.profile.liked_on_facebook,
          gender: resBody.profile.gender,
          phone: resBody.profile.phone,
        },
        localization_lang: resBody.localization_lang,
        roles: resBody.roles,
        level: resBody.level,
        company: resBody.company,
        status: resBody.status,
      };

      checkServerTimeDifference(user);

      const callback = function (_user, _onSuccess) {
        initIntercom(_user);
        updateUser(_user);
        // Update logging context.
        saveUserContext(_user);
        let additionalBrowserInfo = "?";
        if (browser) {
          additionalBrowserInfo =
            browser.browser.name +
            " v" +
            browser.browser.version +
            " on " +
            browser.os.name;
          if (browser.device.model) {
            additionalBrowserInfo += "(" + browser.device.model + ")";
          }
        }
        logInfo("signed in, browser: " + additionalBrowserInfo);
        _onSuccess && _onSuccess(_user);
      };

      if (isUserStudent(user)) {
        user.team = null;
        // Server has a list of teams but it was assumed that user can
        // be a member of only 1 team.
        if (resBody.company && resBody.company.teams && resBody.company.teams[0]) {
          user.team = resBody.company.teams[0];
        }

        const fetchStudentDetails = new Promise(resolve => {
          fetchStudentProfile(
            null,
            resp => {
              user.primary_instructor = resp.primary_instructor;
              user.credits = resp.credits;
              user.student = resp;
              resolve(resp);
            },
            resolve
          );
        });

        Promise.all([fetchStudentDetails]).then(
          () => {
            return callback && callback(user, onSuccess);
          },
          errBody => {
            onError && onError(errBody);
          }
        );
      } else if (isUserTeacher(user)) {
        const fetchInstructorDetails = new Promise(resolve => {
          fetchInstructorProfile(
            null,
            resp => {
              user.instructor = {
                id: resp.instructor_id,
                save_transcription: resp.save_transcription,
              };
              resolve(resp);
            },
            resolve
          );
        });

        Promise.all([fetchInstructorDetails]).then(
          () => {
            return callback && callback(user, onSuccess);
          },
          errBody => {
            onError && onError(errBody);
          }
        );
      } else {
        callback && callback(user, onSuccess);
      }
    })
    .catch(errBody => {
      initIntercom();
      onError && onError(errBody);
    });
}

const UserObservable = new MutableObservable();
export function getUserObservable() {
  return UserObservable;
}

export function updateUser(user) {
  sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  UserObservable.setValue(user);
}

export function useSignout() {
  const navigate = useNavigate();
  return useCallback(() => {
    logInfo("is signing out");
    logOut(
      null,
      resp => {
        clearUserSession();
        if (resp.logout_url) {
          window.location.replace(resp.logout_url);
        } else {
          navigate(TalaeraRoutes.SIGN_IN.path);
        }
      },
      () => {
        clearUserSession();
      }
    );
  }, [navigate]);
}

export function clearUserSession() {
  shutDownIntercom();

  // Clear logging context.
  clearUserContext();

  sessionStorage.removeItem(USER_STORAGE_KEY);
  sessionStorage.removeItem(BETA_ACCEPTED);
  sessionStorage.removeItem(TEMP_NATIVE_LANG);
  deleteRoles();
  clearContentCredentials();
  UserObservable.setValue(undefined);
}

export function getSessionUser() {
  return sessionStorage.getItem(USER_STORAGE_KEY)
    ? JSON.parse(sessionStorage.getItem(USER_STORAGE_KEY))
    : null;
}

function hasRole(role, user) {
  if (!user) {
    user = getSessionUser();
  }
  if (!user) {
    return false;
  }
  return _.contains(user.roles, role);
}

export function hasUserAllOnboardingData(user) {
  if (!user) {
    user = getSessionUser();
  }
  return user?.student?.has_all_onboarding_data;
}

export function isUserTeacher(user) {
  return hasRole("instructor", user);
}

export function isUserStudent(user) {
  return hasRole("student", user);
}

export function isUserAdmin(user) {
  return hasRole("admin", user);
}

export function isUserTeamManager(user) {
  return hasRole("team-manager", user);
}

export function isUserCustomerService(user) {
  return hasRole("customer-service", user);
}

export function isUserCompanyManager(user) {
  return hasRole("company-manager", user);
}

export function isSelfStudyUser(user) {
  if (!user) {
    user = getSessionUser();
  }
  return user && user.student && user.student.type === "self-study";
}

export function isNewUser(user) {
  if (!user) {
    user = getSessionUser();
  }
  return user && user.student && user.isNew;
}

export function isOneOnOneUser(user) {
  if (!user) {
    user = getSessionUser();
  }

  return user && user.student && user.student.type === "one-on-one";
}

export function isGroupUser(user) {
  if (!user) {
    user = getSessionUser();
  }

  return user && user.student && user.student.type === "group";
}

export function isUserOneOnOneStudent(user) {
  return hasRole("one-on-one-student", user);
}

export function isUserContentAdmin(user) {
  // TODO uncomment when a new role will be added.
  // return hasRole("content-admin", user);
  if (
    !Configuration.USERS_TO_EDIT_LIBRARY_CONTENT ||
    Configuration.USERS_TO_EDIT_LIBRARY_CONTENT.length === 0
  ) {
    return false;
  }
  if (!user) {
    user = getSessionUser();
  }
  if (!user) {
    return false;
  }
  return Configuration.USERS_TO_EDIT_LIBRARY_CONTENT.indexOf(user.id) > -1;
}

export function userHasPermissions(permissions) {
  if (!permissions) {
    return true;
  }
  var res = false;
  for (var i = 0; i < permissions.length; i++) {
    if (hasRole(permissions[i])) {
      res = true;
      break;
    }
  }

  return res;
}

export function getUserUTCOffset(dateValue, timezoneValue) {
  const date = dateValue ? dateValue : new Date();
  const zone = timezoneValue ? timezoneValue : getUserTimeZoneId();
  return moment.tz(date, zone).format("Z");
}

export function getUserTimeZoneId() {
  const user = getSessionUser();
  return user.profile.timezone && user.profile.timezone.id
    ? user.profile.timezone.id
    : moment.tz.guess();
}

export function saveTempNativeLanguage(langCode) {
  sessionStorage.setItem(TEMP_NATIVE_LANG, langCode);
}

export function getTempNativeLanguage() {
  return sessionStorage.getItem(TEMP_NATIVE_LANG);
}

export function updateSessionUserProfile(profile) {
  const user = getSessionUser();
  const newProfile = Object.assign({}, profile);
  newProfile.gender = user.profile.gender;
  newProfile.native_language =
    profile.native_language && profile.native_language.code
      ? profile.native_language.code
      : profile.native_language;
  newProfile.timezone.utc_offset = profile.timezone.utc_offset.split(" ")[0];
  user.profile = newProfile;

  sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export function setupUserProfile(profile) {
  const user = getSessionUser();
  const newProfile = Object.assign({}, user.profile);
  newProfile.native_language =
    profile.native_language && profile.native_language.code
      ? profile.native_language.code
      : profile.native_language;
  if (profile.timezone) {
    newProfile.timezone = _.clone(profile.timezone);
  }
  if (profile.title !== undefined) {
    newProfile.title = profile.title;
  }
  if (profile.phone_work !== undefined) {
    newProfile.phone.work = profile.phone_work;
  }
  user.profile = newProfile;

  sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export function updateSessionTeacherProfile(profile) {
  const user = getSessionUser();
  const newProfile = Object.assign({}, user.profile);
  newProfile.bio = profile.bio;
  user.profile = newProfile;

  sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

export function getPrimaryInstructor(user) {
  if (!user) {
    user = getSessionUser();
  }
  if (!user) {
    return null;
  }
  return user.primary_instructor;
}

export function hasPrimaryInstructor(user) {
  const primaryInstructor = getPrimaryInstructor(user);
  return primaryInstructor !== null && primaryInstructor !== undefined;
}

function checkServerTimeDifference(user) {
  // Checks if time difference between server time and user's local time is more than ${SERVER_TIME_DIFFERENCE}.
  if (!isUserStudent(user) && !isUserTeacher(user)) {
    return;
  }
  const offset = getServerTimeOffset();
  if (
    offset !== undefined &&
    offset !== null &&
    Math.abs(offset) > SERVER_TIME_DIFFERENCE
  ) {
    handleServerTimeDifference({
      user: user,
      offset: offset,
    });
  }
}
